import type { Bid, BidScore, ProjectPriorities, ScopeItem } from './types'

const coverageValue = { included: 1, allowance: 0.58, unclear: 0.28, excluded: 0, 'not-applicable': 1 }
const clamp = (value: number) => Math.max(0, Math.min(100, value))

function normalizedWeights(priorities: ProjectPriorities) {
  const sum = Object.values(priorities).reduce((total, value) => total + Math.max(0, value), 0) || 1
  return Object.fromEntries(Object.entries(priorities).map(([key, value]) => [key, Math.max(0, value) / sum])) as Record<keyof ProjectPriorities, number>
}

export function scoreBids(scope: ScopeItem[], bids: Bid[], priorities: ProjectPriorities): BidScore[] {
  if (!bids.length) return []
  const weights = normalizedWeights(priorities)
  const prices = bids.map((bid) => bid.total).filter((price) => price > 0)
  const lowest = Math.min(...prices)
  const fastest = Math.min(...bids.map((bid) => bid.durationDays).filter((days) => days > 0))
  const required = scope.filter((scopeItem) => scopeItem.importance === 'required')

  return bids.map((bid) => {
    const itemMap = new Map(bid.items.map((bidItem) => [bidItem.scopeItemId, bidItem]))
    let possible = 0
    let earned = 0
    let coveredRequired = 0
    let ambiguityCount = 0
    for (const scopeItem of scope) {
      const importanceWeight = scopeItem.importance === 'required' ? 3 : scopeItem.importance === 'preferred' ? 2 : 1
      const coverage = itemMap.get(scopeItem.id)?.coverage ?? 'unclear'
      possible += importanceWeight
      earned += coverageValue[coverage] * importanceWeight
      if (scopeItem.importance === 'required' && coverage === 'included') coveredRequired += 1
      if (coverage === 'unclear' || coverage === 'allowance') ambiguityCount += 1
    }

    const scopeScore = clamp((earned / Math.max(1, possible)) * 100)
    const priceScore = bid.total > 0 ? clamp((lowest / bid.total) * 100) : 0
    const warrantyScore = clamp((Math.min(bid.laborWarrantyYears, 10) / 10) * 55 + (Math.min(bid.productWarrantyYears, 25) / 25) * 45)
    const timelineScore = bid.durationDays > 0 ? clamp((fastest / bid.durationDays) * 100) : 0
    const confidenceScore = clamp(
      (bid.licenseVerified ? 28 : 0) +
      (bid.insuranceVerified ? 28 : 0) +
      (1 - ambiguityCount / Math.max(1, scope.length)) * 34 +
      (bid.status === 'revised' ? 10 : bid.status === 'clarifying' ? 5 : 0),
    )
    const components = { scope: scopeScore, price: priceScore, warranty: warrantyScore, timeline: timelineScore, confidence: confidenceScore }
    const total = clamp(Object.entries(components).reduce((result, [key, value]) => result + value * weights[key as keyof ProjectPriorities], 0))
    const riskFlags: string[] = []
    if (bid.deposit > bid.total * 0.35) riskFlags.push('Large upfront payment exposure')
    if (!bid.licenseVerified) riskFlags.push('License not yet verified')
    if (!bid.insuranceVerified) riskFlags.push('Insurance not yet verified')
    if (coveredRequired < required.length) riskFlags.push(`${required.length - coveredRequired} required scope items not clearly included`)
    if (ambiguityCount >= Math.max(2, Math.ceil(scope.length * 0.3))) riskFlags.push('High number of allowances or unclear items')
    return { bidId: bid.id, total: Math.round(total), components, coveredRequired, requiredCount: required.length, ambiguityCount, riskFlags }
  }).sort((left, right) => right.total - left.total)
}

export function projectReadiness(scope: ScopeItem[], bids: Bid[]) {
  const definedScope = scope.filter((scopeItem) => scopeItem.requirement.trim().length >= 12).length
  const scopeScore = (definedScope / Math.max(1, scope.length)) * 55
  const bidScore = Math.min(25, bids.length * 8.34)
  const clarity = bids.length ? bids.reduce((sum, bid) => sum + bid.items.filter((item) => item.coverage !== 'unclear').length / Math.max(1, scope.length), 0) / bids.length : 0
  return Math.round(clamp(scopeScore + bidScore + clarity * 20))
}
